import { useCallback, useEffect, useRef } from 'react'
import { PRMarinaFileArtboard } from '@sketch-hq/sketch-web-renderer'

import { ArtboardOverlayProps } from '../../../../canvas/components/ArtboardOverlay'
import { useSelectedFrame } from '../../../../canvas/components/SelectedFrameContext'
import { usePlaceDraftAnnotation } from '../../../../annotations/hooks'
import { useStableHandler, noop } from '@sketch/utils'
import { useNavigateToFrame } from '../../../../canvas/hooks'

type FramesDict = Record<string, HTMLDivElement | null>

/**
 * Hook that manages view mode state and provides frame refs for DOM tracking.
 * Uses SelectedFrameContext to share the selected frame UUID with other components.
 */
export function useViewModeState() {
  const framesRefs = useRef<FramesDict>({})
  const selectedFrameContext = useSelectedFrame()

  // Fall back to no-op if context is not available
  const selectedArtboardUUID = selectedFrameContext?.selectedFrameUUID ?? null
  const setSelectedArtboardUUID =
    selectedFrameContext?.setSelectedFrameUUID ?? noop

  return {
    framesRefs,
    selectedArtboardUUID,
    setSelectedArtboardUUID,
  }
}

/**
 * Hook that returns props specific to view mode for the ArtboardOverlay component.
 */
export function useViewModeOverlayProps(
  frame: PRMarinaFileArtboard,
  selectedArtboardUUID: string | null,
  setSelectedArtboardUUID: (uuid: string | null) => void,
  isPlacingDraftAnnotation: boolean
): Partial<ArtboardOverlayProps> {
  const navigateToFrame = useNavigateToFrame()

  const isSelected = selectedArtboardUUID === frame.objectId

  const handleSelectFrame = useStableHandler(() => {
    if (!isSelected) {
      setSelectedArtboardUUID(frame.objectId)
    }
  })

  const doubleClickHandler = useStableHandler(() => {
    navigateToFrame(frame.objectId, 'doubleClick')
  })

  const handleCTAClick = useStableHandler(() => {
    if (isSelected) {
      navigateToFrame(frame.objectId, 'CTA')
    }
  })

  if (isPlacingDraftAnnotation) {
    return {
      selected: isSelected,
      disableAutoHover: true,
    }
  }

  return {
    selected: isSelected,
    disableAutoHover: false,
    // Note: We are setting handlers to undefined when not needed as having a value
    // or not controls if the cursor is a pointer or not in the FrameOverlay component.
    onOverlayClick: !isSelected ? handleSelectFrame : undefined,
    onOverlayDoubleClick: doubleClickHandler,
    onCtaClick: isSelected ? handleCTAClick : undefined,
  }
}

/**
 * Hook that handles global events for frame overlays in view mode (keyboard shortcuts and click outside).
 * Only active when not in inspector mode.
 */
export function useViewModeGlobalEvents(
  framesRefs: React.RefObject<FramesDict>,
  selectedArtboardUUID: string | null,
  setSelectedArtboardUUID: (uuid: string | null) => void,
  isInspectorActive: boolean
) {
  const navigateToFrame = useNavigateToFrame()
  const [isPlacingDraftAnnotation] = usePlaceDraftAnnotation() || []

  /**
   * These function covers these scenarios:
   * - When an artboard is selected and the user is writing a comment, when the
   *   user clicks `⌘ + Enter` it should publish the comment, not navigate to
   *   the artboard
   * - When an artboard is selected and the user is writing a comment, when the
   *   user clicks `Enter` it should add a new line, not navigate to the artboard
   */
  const goToArtboardGuard: typeof navigateToFrame = useCallback(
    (...args) => {
      if (isPlacingDraftAnnotation) {
        return
      }

      navigateToFrame(...args)
    },
    [navigateToFrame, isPlacingDraftAnnotation]
  )

  const handleWindowKeyPress = useStableHandler((event: KeyboardEvent) => {
    if (!selectedArtboardUUID) {
      return
    }

    if (event.key === 'Enter' && document.activeElement) {
      goToArtboardGuard(selectedArtboardUUID!, 'keyPressEnter')
    } else if (event.key === 'Escape') {
      setSelectedArtboardUUID(null)
    }
  })

  // Handle click outside to deselect the currently selected frame
  const handleWindowClick = useStableHandler(
    (event: MouseEvent | TouchEvent) => {
      // Checking if the click on the overlay was inside one of the artboards,
      // If not, we deselect the selected artboard
      for (const frameId in framesRefs.current) {
        if (framesRefs.current[frameId]?.contains(event.target as Node)) {
          return
        }
      }

      // if zoom or pages dropdown is clicked, we don't want to deselect the artboard
      const zoomDropdown = document.querySelector('[data-testid="zoom-toggle"]')
      const pagesDropdown = document.querySelector(
        '[data-testid="pages-selector-trigger"]'
      )

      const isZoomDropdownClicked = zoomDropdown?.contains(event.target as Node)
      const isPagesDropdownClicked = pagesDropdown?.contains(
        event.target as Node
      )

      if (isZoomDropdownClicked || isPagesDropdownClicked) {
        return
      }

      if (selectedArtboardUUID) setSelectedArtboardUUID(null)
    }
  )

  useEffect(() => {
    // Only attach event listeners in view mode
    if (isInspectorActive || !selectedArtboardUUID) {
      return
    }

    window.document.body.addEventListener('keydown', handleWindowKeyPress)
    window.document.body.addEventListener('click', handleWindowClick)

    return () => {
      window.document.body.removeEventListener('keydown', handleWindowKeyPress)
      window.document.body.removeEventListener('click', handleWindowClick)
    }
  }, [
    handleWindowClick,
    handleWindowKeyPress,
    selectedArtboardUUID,
    isInspectorActive,
  ])
}
